import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';

export const GENERIC_ERROR_KEY = 'errors.An error has occured';

interface ApiErrorBody {
  error?: unknown;
  message?: unknown;
  errors?: Record<string, unknown>;
}

// Raw backend messages that can't be used as translation keys directly (e.g. they contain dots)
const MESSAGE_KEY_ALIASES: Record<string, string> = {
  'The POST data is too large.': 'post_data_too_large',
  'Invalid phone number format. Use international format like +41791234567.':
    'invalid_phone_number_format',
};

export function errorMessageKey(message: string): string {
  return MESSAGE_KEY_ALIASES[message] ?? message;
}

export type ErrorTranslations = Record<
  string,
  string | Record<string, string> | undefined
>;

/**
 * Text for an error toast in the active language (German, Italian, or French).
 * Billing failures use a stable code. Unknown API text is not shown.
 */
export function visibleApiErrorText(
  body: unknown,
  errorsTranslations: ErrorTranslations | undefined,
  generic: string
): string {
  const billingText = billingFailureText(body, errorsTranslations);
  if (billingText !== null) {
    return billingText;
  }

  const messages = apiErrorMessages(body);
  const genericText = generic.trim();

  const translated = messages
    .map(message => errorsTranslations?.[errorMessageKey(message)])
    .find(
      (text): text is string =>
        typeof text === 'string' &&
        text.trim() !== '' &&
        text.trim() !== genericText
    );

  if (translated) {
    return translated.trim();
  }

  return genericText;
}

function billingFailureText(
  body: unknown,
  errorsTranslations: ErrorTranslations | undefined
): string | null {
  if (!body || typeof body !== 'object' || !('failures' in body)) {
    return null;
  }

  const failures = (body as { failures?: unknown }).failures;
  if (!Array.isArray(failures) || failures.length === 0) {
    return null;
  }

  const buckets: BillingFailureBucket[] = [];
  const bucketIndex = new Map<string, BillingFailureBucket>();

  for (const failure of failures) {
    if (!failure || typeof failure !== 'object') {
      return null;
    }

    const record = failure as {
      code?: unknown;
      company_id?: unknown;
      params?: unknown;
    };
    const code = typeof record.code === 'string' ? record.code : 'unexpected';
    const template =
      billingTemplate(errorsTranslations, code) ??
      billingTemplate(errorsTranslations, 'unexpected');
    if (template === null) {
      return null;
    }

    const params = failureParams(record.params);
    const bucketKey = `${code}:${JSON.stringify(params)}`;
    let bucket = bucketIndex.get(bucketKey);
    if (bucket === undefined) {
      bucket = { template, companyIds: [], params };
      bucketIndex.set(bucketKey, bucket);
      buckets.push(bucket);
    }

    const companyId = companyIdText(record.company_id);
    if (companyId !== '') {
      bucket.companyIds.push(companyId);
    }
  }

  return buckets
    .map(bucket =>
      interpolate(bucket.template, {
        ...bucket.params,
        companyId: bucket.companyIds.join(', '),
      })
    )
    .join('\n');
}

interface BillingFailureBucket {
  template: string;
  companyIds: string[];
  params: Record<string, string | number>;
}

/**
 * Transloco stores nested JSON as flat keys, so the billing texts live at
 * "abacusBilling.missing_billing_email" inside the errors group.
 */
function billingTemplate(
  errorsTranslations: ErrorTranslations | undefined,
  code: string
): string | null {
  const flat = errorsTranslations?.[`abacusBilling.${code}`];
  if (typeof flat === 'string' && flat.trim() !== '') {
    return flat;
  }

  const nested = errorsTranslations?.['abacusBilling'];
  if (nested === undefined || typeof nested === 'string') {
    return null;
  }

  const template = nested[code];
  if (typeof template !== 'string' || template.trim() === '') {
    return null;
  }

  return template;
}

function failureParams(value: unknown): Record<string, string | number> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const params: Record<string, string | number> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string' || typeof entry === 'number') {
      params[key] = entry;
    }
  }

  return params;
}

function companyIdText(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'string' && value.trim() !== '') {
    return value.trim();
  }

  return '';
}

function interpolate(
  template: string,
  params: Record<string, string | number>
): string {
  return template.replace(
    /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g,
    (_match, name: string) => {
      const value = params[name];
      return value === undefined ? '' : String(value);
    }
  );
}

function apiErrorMessages(body: unknown): string[] {
  if (!body || typeof body !== 'object') {
    return [];
  }

  const record = body as ApiErrorBody & {
    failures?: { company_id?: unknown; reason?: unknown }[];
  };
  const messages: string[] = [];

  // Field errors first: they are more specific than a generic code like "validation_failed"
  if (record.errors && typeof record.errors === 'object') {
    for (const fieldMessages of Object.values(record.errors)) {
      if (!Array.isArray(fieldMessages)) {
        continue;
      }
      const message = fieldMessages.find(item => typeof item === 'string');
      if (typeof message === 'string') {
        messages.push(message);
      }
    }
  }
  if (typeof record.message === 'string') {
    messages.push(record.message);
  }
  if (typeof record.error === 'string') {
    messages.push(record.error);
  }

  if (messages.length === 0 && Array.isArray(record.failures)) {
    for (const failure of record.failures) {
      if (typeof failure?.reason === 'string' && failure.reason.trim() !== '') {
        const companyId =
          typeof failure.company_id === 'number'
            ? failure.company_id
            : undefined;
        messages.push(
          companyId !== undefined &&
            !failure.reason.includes(`Company #${companyId}`)
            ? `Company #${companyId}: ${failure.reason}`
            : failure.reason
        );
      }
    }
  }

  return messages;
}

export interface ParsedApiError {
  alertKey: string;
  fieldErrors: Record<string, string>;
  messages: string[];
}

export function parseApiError(
  err: unknown,
  fields: readonly string[],
  translocoService: TranslocoService
): ParsedApiError {
  const body = (err instanceof HttpErrorResponse ? err.error : undefined) as
    ApiErrorBody | undefined;
  const messages: string[] = [];
  const fieldErrors: Record<string, string> = {};

  // Field errors first: they are more specific than a generic code like "validation_failed"
  if (body?.errors && typeof body.errors === 'object') {
    for (const [field, fieldMessages] of Object.entries(body.errors)) {
      const message = Array.isArray(fieldMessages)
        ? fieldMessages.find(m => typeof m === 'string')
        : undefined;
      if (!message) continue;
      messages.push(message);
      if (fields.includes(field)) {
        fieldErrors[field] = errorTranslationKey(message, translocoService);
      }
    }
  }
  if (typeof body?.error === 'string') {
    messages.push(body.error);
  }
  if (typeof body?.message === 'string') {
    messages.push(body.message);
  }

  // Use the first message that has a translation, e.g. skip a generic "error" in favour of a specific message
  const alertKey =
    messages
      .map(message => errorTranslationKey(message, translocoService))
      .find(key => key !== GENERIC_ERROR_KEY) ?? GENERIC_ERROR_KEY;

  return {
    alertKey,
    fieldErrors,
    messages,
  };
}

// Backend messages are keys of the "errors" translation group; unknown ones fall back to a generic message
export function errorTranslationKey(
  message: string,
  translocoService: TranslocoService
): string {
  const translation = translocoService.getTranslation(
    translocoService.getActiveLang()
  ) as Record<string, unknown>;
  const errors = translation['errors'] as Record<string, unknown> | undefined;
  const errorKey = errorMessageKey(message);
  const key = `errors.${errorKey}`;
  return translation[key] || errors?.[errorKey] ? key : GENERIC_ERROR_KEY;
}

// Laravel "max" rule on uploads, e.g. "The files.0 field must not be greater than 20480 kilobytes."
const FILE_TOO_LARGE_PATTERN =
  /^The (.+) field must not be greater than (\d+) kilobytes\.$/;

/**
 * Builds a user friendly message naming the files that exceed the upload limit.
 * The file names are looked up in the uploaded FormData via the field path (files.0 -> files[] index 0).
 */
export function fileTooLargeMessage(
  errors: Record<string, unknown> | undefined,
  requestBody: unknown,
  translocoService: TranslocoService
): string | undefined {
  if (!errors || typeof errors !== 'object') return undefined;

  const fileNames: string[] = [];
  let maxKilobytes = 0;
  for (const [field, fieldMessages] of Object.entries(errors)) {
    const messages = Array.isArray(fieldMessages) ? fieldMessages : [];
    for (const message of messages) {
      const match =
        typeof message === 'string'
          ? FILE_TOO_LARGE_PATTERN.exec(message)
          : null;
      if (!match) continue;
      maxKilobytes = Math.max(maxKilobytes, Number(match[2]));
      fileNames.push(uploadedFileName(field, requestBody));
    }
  }
  if (!fileNames.length) return undefined;

  const params = {
    fileNames: fileNames.join(', '),
    maxSize: Math.round(maxKilobytes / 1024),
  };
  return translocoService.translate(
    fileNames.length === 1 ? 'errors.file_too_large' : 'errors.files_too_large',
    params
  );
}

function uploadedFileName(field: string, requestBody: unknown): string {
  const match = /^(.+)\.(\d+)$/.exec(field);
  const name = match ? match[1] : field;
  const index = match ? Number(match[2]) : undefined;
  if (requestBody instanceof FormData) {
    const value =
      index !== undefined
        ? requestBody.getAll(`${name}[]`)[index]
        : requestBody.get(name);
    if (value instanceof File) return `«${value.name}»`;
  }
  return index !== undefined ? `#${index + 1}` : name;
}

const INVALID_LESSON_FIELD_PATTERN =
  /^The selected units\.(\d+)\.lessons\.(\d+)\.(\w+) is invalid\.$/;

export function invalidLessonFieldMessage(
  errors: Record<string, unknown> | undefined,
  translocoService: TranslocoService
): string | undefined {
  if (!errors || typeof errors !== 'object') return undefined;

  const messages: string[] = [];
  for (const fieldMessages of Object.values(errors)) {
    const list = Array.isArray(fieldMessages) ? fieldMessages : [];
    for (const message of list) {
      const match =
        typeof message === 'string'
          ? INVALID_LESSON_FIELD_PATTERN.exec(message)
          : null;
      if (!match) continue;
      const [, unit, lesson, field] = match;
      const fieldKey = `courseLesson.${field}`;
      const fieldLabel = translocoService.translate(fieldKey);
      messages.push(
        translocoService.translate('errors.lesson_field_invalid', {
          field: fieldLabel === fieldKey ? field : fieldLabel,
          unit: Number(unit) + 1,
          lesson: Number(lesson) + 1,
        })
      );
    }
  }
  return messages.length ? messages.join('\n') : undefined;
}
